import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { toUiError } from '../lib/errors.ts';
import type { UiError } from '../lib/errors.ts';
import { useFocusReturn } from '../lib/focus.ts';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../primitives/alert-dialog.tsx';
import { Button } from './Button.tsx';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A question naming the exact object: `Stop session "Fix the login race"?` */
  title: ReactNode;
  /** One sentence of consequence, including the blast radius: "Its 3 running subagents stop too." */
  description: ReactNode;
  /** The verb, matching the title: "Stop session". Never "OK" or "Yes". */
  confirmLabel: string;
  /** The safe choice. Default "Cancel"; better is what keeps things as they are ("Keep running"). */
  cancelLabel?: string;
  /** danger for destructive actions. */
  tone?: 'danger' | 'primary';
  /** Runs on confirm. While it runs the button spins; on success the dialog closes; on failure the error shows inline. */
  onConfirm: () => void | Promise<void>;
  /** Extra detail under the description (for example the machine and path). */
  children?: ReactNode;
}

/**
 * Confirm an irreversible, consequential action, on the shadcn AlertDialog (components.md):
 * 440 wide, focus starts on the safe button, Esc closes, a backdrop click does not, no X.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  // Opened from state, not a trigger: give focus back to what opened it.
  const focusReturn = useFocusReturn(open);

  useEffect(() => {
    if (open) {
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => (!busy || v ? onOpenChange(v) : undefined)}>
      <AlertDialogContent
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
        onOpenAutoFocus={focusReturn.onOpenAutoFocus}
        onCloseAutoFocus={focusReturn.onCloseAutoFocus}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        {error && (
          <div role="alert" className="flex gap-2 rounded-card border border-destructive-line bg-destructive-soft p-3 text-ui">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-status-danger" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-destructive-ink">{error.what}</p>
              <p className="mt-0.5 text-fg-muted">{error.why}</p>
              <p className="mt-0.5 text-fg-muted">{error.next}</p>
            </div>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={busy} onClick={confirm}>
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
